import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, Link2, CheckCircle2, X } from "lucide-react";

// Strava brand color
const STRAVA = "#FC4C02";

export const StravaConnect = ({ onSynced }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/strava/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ connected: false })).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/strava/authorize", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Strava connect");
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/strava/sync");
      toast.success(data.imported > 0 ? `Imported ${data.imported} run${data.imported === 1 ? "" : "s"} from Strava` : "You're all synced up");
      await load();
      if (onSynced) onSynced();
    } catch (e) {
      toast.error("Sync failed");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/strava/disconnect");
      toast.success("Disconnected from Strava");
      await load();
    } catch (e) {
      toast.error("Could not disconnect");
    } finally { setBusy(false); }
  };

  if (loading) return null;

  if (!status?.connected) {
    return (
      <Card data-testid="strava-connect-card" className="rounded-2xl bg-card border-border p-4 mb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-sm flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full" style={{ background: STRAVA }} /> Sync from Apple Watch & Garmin
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Connect Strava to auto-import your device runs.</p>
          </div>
          <Button data-testid="strava-connect-button" onClick={connect} disabled={busy} className="text-foreground font-semibold flex-shrink-0" style={{ background: STRAVA }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2 className="h-4 w-4 mr-1.5" /> Connect Strava</>}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card data-testid="strava-connected-card" className="rounded-2xl bg-card border-border p-4 mb-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-sm flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" style={{ color: STRAVA }} /> Strava connected
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {status.last_sync ? `Last sync ${new Date(status.last_sync).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : "Runs from your devices sync automatically."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button data-testid="strava-sync-button" onClick={sync} disabled={busy} size="sm" className="bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Sync</>}
          </Button>
          <button data-testid="strava-disconnect-button" onClick={disconnect} disabled={busy} className="text-muted-foreground hover:text-red-400 transition-colors" title="Disconnect Strava">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Card>
  );
};

export default StravaConnect;
